import { Formik, Form, Field, ErrorMessage } from "formik";
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useAlert } from "../../contexts/AlertContext";
import * as Yup from "yup";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { CanvasContext } from "../../contexts/CanvasContext";

const initialValues = {
  email: "",
  password: "",
};

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/,
      "Password must contain at least one letter, one number, and one special character, and be at least 6 characters long"
    ),
});

const forgotPassSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

const resetPassSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  otp: Yup.string()
    .length(6, "OTP must be 6 digits")
    .required("OTP is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/,
      "Password must contain at least one letter, one number, and one special character, and be at least 6 characters long"
    ),
});

const Login = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { setAlert } = useAlert();
  const { isDarkTheme } = useContext(CanvasContext);
  const [forgotPass, setForgotPass] = useState(false);
  const [resetPass, setResetPass] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const sendOTP = async (values) => {
    try {
      const response = await fetch(
        // "http://localhost:5000/api/auth/forgot-password",
        "https://dnd-back.vercel.app/api/auth/forgot-password",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: values.email,
          }),
        }
      );
      const json = await response.json();
      if (json.success) {
        setAlert({
          title: "OTP Sent",
          message: "Please check your email for the OTP. Valid for 5 minutes.",
          variant: "success",
          duration: 5000,
        });
        setResetPass(true);
      } else {
        setAlert({
          title: "OTP Sending Failed",
          message: json.error,
          variant: "error",
          duration: 5000,
        });
      }
    } catch (error) {
      setAlert({
        title: "An error occurred",
        message: "Please try again later.",
        variant: "error",
        duration: 5000,
      });
      console.error("An error occurred:", error);
    }
  };

  const resetPassword = async (values) => {
    try {
      const response = await fetch(
        // "http://localhost:5000/api/auth/reset-password",
        "https://dnd-back.vercel.app/api/auth/reset-password",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: values.email,
            otp: values.otp,
            newPassword: values.newPassword,
          }),
        }
      );
      const json = await response.json();
      if (json.success) {
        setAlert({
          title: "Password Reset Successful",
          message: "You can now log in with your new password.",
          variant: "success",
          duration: 5000,
        });
        setForgotPass(false);
        setResetPass(false);
      } else {
        setAlert({
          title: "Password Reset Failed",
          message: json.error,
          variant: "error",
          duration: 5000,
        });
      }
    } catch (error) {
      setAlert({
        title: "An error occurred",
        message: "Please try again later.",
        variant: "error",
        duration: 5000,
      });
      console.error("An error occurred:", error);
    }
  };

  const onSignin = async (values) => {
    try {
      const response = await fetch(
        // "http://localhost:5000/api/auth/login",
        "https://dnd-back.vercel.app/api/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        }
      );

      const result = await response.json();

      if (result.success) {
        login(result.user);
        setAlert({
          title: "You have successfully Signed in!",
          message: "You are now logged in",
          variant: "success",
          duration: 1000,
        });
        navigate("/profile");
      } else {
        setAlert({
          title: "Login Failed",
          message: result.error,
          variant: "error",
          duration: 1000,
        });
      }
    } catch (error) {
      console.error("An error occurred during sign in:", error);
      setAlert({
        title: "An error occurred",
        message: "Please try again later.",
        variant: "error",
        duration: 1000,
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <React.Fragment>
      {!user && (
        <section
          className={`signup-page ${isDarkTheme ? "dark-theme" : ""}`}
          id="signin"
        >
          <div className="signup-container">
            <header className="signup-header">
              <h1>{forgotPass ? "Reset Password" : "Welcome Back"}</h1>
              <p>
                {forgotPass ? "Reset your password" : "Sign in to get started"}
              </p>
            </header>
            <Formik
              initialValues={initialValues}
              validationSchema={
                forgotPass
                  ? resetPass
                    ? resetPassSchema
                    : forgotPassSchema
                  : validationSchema
              }
              onSubmit={
                forgotPass ? (resetPass ? resetPassword : sendOTP) : onSignin
              }
            >
              {(formik) => (
                <Form className="signup-form">
                  <div className="form-group">
                    <label htmlFor="email">
                      <FaEnvelope size={20} />
                      Email
                    </label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      className={
                        formik.touched.email && formik.errors.email
                          ? "input-error"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="error"
                    />
                  </div>

                  {!forgotPass && (
                    <div className="form-group">
                      <label htmlFor="password">
                        <FaLock size={20} />
                        Password
                      </label>
                      <div className="password-input">
                        <Field
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          autoComplete="current-password"
                          className={
                            formik.touched.password && formik.errors.password
                              ? "input-error"
                              : ""
                          }
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="password-toggle"
                        >
                          {showPassword ? (
                            <FaEyeSlash size={20} />
                          ) : (
                            <FaEye size={20} />
                          )}
                        </button>
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="error"
                      />
                    </div>
                  )}

                  {resetPass && (
                    <>
                      <div className="form-group">
                        <label htmlFor="otp">OTP</label>
                        <Field
                          type="text"
                          id="otp"
                          name="otp"
                          autoComplete="one-time-code"
                          className={
                            formik.touched.otp && formik.errors.otp
                              ? "input-error"
                              : ""
                          }
                        />
                        <ErrorMessage
                          name="otp"
                          component="div"
                          className="error"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="newPassword">
                          <FaLock size={20} />
                          New Password
                        </label>
                        <div className="password-input">
                          <Field
                            type={showPassword ? "text" : "password"}
                            id="newPassword"
                            name="newPassword"
                            autoComplete="new-password"
                            className={
                              formik.touched.newPassword &&
                              formik.errors.newPassword
                                ? "input-error"
                                : ""
                            }
                          />
                          <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="password-toggle"
                          >
                            {showPassword ? (
                              <FaEyeSlash size={20} />
                            ) : (
                              <FaEye size={20} />
                            )}
                          </button>
                        </div>
                        <ErrorMessage
                          name="newPassword"
                          component="div"
                          className="error"
                        />
                      </div>
                    </>
                  )}

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    {formik.isSubmitting
                      ? "Processing..."
                      : forgotPass
                      ? resetPass
                        ? "Reset Password"
                        : "Send OTP"
                      : "Login"}
                  </button>

                  <div className="signup-link">
                    {!forgotPass && (
                      <>
                        <Link to="/signup">Don't have an account? Sign Up</Link>
                        <br />
                        <Link onClick={() => setForgotPass(true)}>
                          Forgot password?
                        </Link>
                      </>
                    )}
                    {forgotPass && (
                      <Link
                        onClick={() => {
                          setForgotPass(false);
                          setResetPass(false);
                        }}
                      >
                        &larr; Go back to Login
                      </Link>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </section>
      )}
    </React.Fragment>
  );
};

export default Login;
